/**
 * StreamingDmBubble
 *
 * Synthetic message bubble that renders the in-flight agent reply for a
 * coworker DM, driven by `agentDmStreamStore` (ephemeral state populated
 * by SSE events from `/api/agent/chat`). Disappears when the SSE stream
 * finishes — at which point the server-side tee accumulator has already
 * INSERTed the real message and Zero replication takes over.
 *
 * Mirrors what iOS does in `MessageListView.swift` with its synthetic
 * streaming message item.
 */

import { useEffect, useMemo, useState } from "react";
import {
  agentDmStreamStore,
  useAgentDmStream,
} from "@/lib/agent-dm-stream-store";
import { abortAgentDmStream } from "@/lib/agent-dm-stream-client";
import { ThinkingSection } from "@/components/ui/thinking-section";
import { ThinkingLabel } from "@/components/terminal-v2/ThinkingLabel";
import { LoadingIcon } from "@/components/ui/loading-icon";
import { CoworkerIcon } from "@/components/ui/coworker-icon";
import { AnimatedImage } from "@/components/ui/animated-image";
import { renderMessageContent } from "@/components/messages/message-cards";
import { TEXT_MESSAGE_AUTHOR } from "@/lib/surface-styles";
import type { RenderUser } from "@/lib/render-user";

/** Skip-if-fast window: dropped to 0 after iOS UX feedback — the
 *  user wants the "Thinking…" shimmer pill to appear IMMEDIATELY
 *  when a message is sent to a coworker. The pill is already
 *  replaced seamlessly by the thinking chip the moment the first
 *  delta arrives, so there's no flicker to worry about.
 *
 *  Kept as a constant (instead of removing the logic) so we can
 *  dial it back up if future telemetry shows flicker. */
const SKIP_IF_FAST_MS = 0;

export function StreamingDmBubble({
  channelId,
  renderUserMap,
  latestMessageUserId,
  latestMessageCreatedAt,
  workspaceMembers,
  channels,
  onUserClick,
  onChannelClick,
}: {
  channelId: string;
  renderUserMap: Map<string, RenderUser>;
  /** user_id of the most recent message in this channel (Zero-replicated). */
  latestMessageUserId: string | null;
  /** created_at (ms) of the most recent message in this channel. */
  latestMessageCreatedAt: number | null;
  /** Forwarded to `renderMessageContent` so streaming markdown matches
   *  the real DB row's rendering (mentions, channel links, etc.). */
  workspaceMembers?: Array<{ id: string; display_name: string }>;
  channels?: Array<{ id: string; name: string }>;
  onUserClick?: (userId: string) => void;
  onChannelClick?: (channelId: string) => void;
}) {
  const stream = useAgentDmStream(channelId);

  // Skip-if-fast: state-driven instead of `Date.now()` (which would
  // violate `react-hooks/purity`). We store the `startedAt` value the
  // window has expired *for*; the bubble counts the window as elapsed
  // only when that recorded value matches the current turn's
  // `startedAt`. This naturally resets to "not elapsed" whenever a new
  // turn starts, without needing a second setState call inside the
  // effect (which would violate `react-hooks/set-state-in-effect`).
  const [windowElapsedFor, setWindowElapsedFor] = useState<number | null>(null);
  const startedAt = stream?.startedAt;
  useEffect(() => {
    if (startedAt == null) return;
    const t = setTimeout(
      () => setWindowElapsedFor(startedAt),
      SKIP_IF_FAST_MS + 20,
    );
    return () => clearTimeout(t);
  }, [startedAt]);
  const skipFastWindowOver =
    startedAt != null && windowElapsedFor === startedAt;

  // Resolve the coworker's display info from the user map. The lookup is
  // memoised on the coworkerId so re-renders driven by content updates
  // don't re-derive it on every keystroke.
  const coworker = useMemo<RenderUser | null>(() => {
    if (!stream) return null;
    return renderUserMap.get(stream.coworkerId) ?? null;
  }, [stream, renderUserMap]);

  // Auto-clear when the real DB row arrives via Zero replication. This
  // covers two cases the SSE consumer can't handle on its own:
  //   1. The SSE call returned 500 / errored mid-stream but the server
  //      eventually posts a real reply (e.g. via the scheduler fallback)
  //      — without this, the synthetic error bubble would linger forever
  //      alongside the real message.
  //   2. Network drop after the server already inserted the message
  //      — the synthetic bubble's `finish()` never fires because the SSE
  //      reader never closes cleanly.
  // The condition: a fresh agent message authored by our streaming
  // coworker that arrived AFTER this turn started.
  useEffect(() => {
    if (!stream) return;
    if (latestMessageUserId !== stream.coworkerId) return;
    if (latestMessageCreatedAt == null) return;
    if (latestMessageCreatedAt < stream.startedAt) return;
    // Abort the still-open SSE reader first so it doesn't keep
    // consuming bytes into a cleared store. Server-side tee
    // accumulator keeps running regardless — the post already
    // happened by this point anyway.
    abortAgentDmStream(channelId);
    agentDmStreamStore.forceClear(channelId);
  }, [stream, latestMessageUserId, latestMessageCreatedAt, channelId]);

  if (!stream) return null;

  // Synchronous short-circuit: if the real Zero-replicated row from
  // the streaming coworker has already arrived for this turn, suppress
  // the synthetic bubble immediately. The useEffect above also calls
  // forceClear() so the store entry gets removed on the next tick, but
  // returning null here closes the brief render-vs-effect window where
  // the user would otherwise see both the synthetic bubble and the
  // real row simultaneously for a single frame.
  if (
    latestMessageUserId === stream.coworkerId &&
    latestMessageCreatedAt != null &&
    latestMessageCreatedAt >= stream.startedAt
  ) {
    return null;
  }

  const displayName = coworker?.display_name ?? "Coworker";
  const initials = displayName.slice(0, 2).toUpperCase();
  const hasContent = stream.content.trim().length > 0;
  const hasThinking = stream.thinking.trim().length > 0;

  // Skip-if-fast: while we have nothing to show AND the
  // SKIP_IF_FAST_MS window hasn't elapsed yet, render nothing. With
  // SKIP_IF_FAST_MS = 0 (current default) this check is effectively
  // disabled — the pill appears on the very first render. Kept the
  // guard so future non-zero values still work correctly.
  if (
    SKIP_IF_FAST_MS > 0 &&
    !hasContent &&
    !hasThinking &&
    !stream.error &&
    !skipFastWindowOver
  ) {
    return null;
  }

  return (
    <div
      data-streaming-dm-bubble
      className="group px-4 py-2"
      key={stream.triggerMessageId}
    >
      <div className="relative flex gap-3 p-2 rounded-lg">
        <div className="h-9 w-9 rounded-full bg-foreground/20 flex items-center justify-center text-xs font-semibold shrink-0 overflow-hidden">
          {coworker?.avatar_url ? (
            <AnimatedImage
              src={coworker.avatar_url}
              alt={displayName}
              className="h-full w-full object-cover"
            />
          ) : (
            initials
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline gap-2 mb-1">
            <span className={TEXT_MESSAGE_AUTHOR}>{displayName}</span>
            <CoworkerIcon size={12} className="shrink-0" />
          </div>

          {/*
            Lifecycle (matches the locked UX spec):
              0. T<400ms with no content: render nothing (skip-if-fast)
              1. First thinking_delta arrives → ThinkingSection appears
                 expanded with spinner + "Thinking…" header
              2. First content delta arrives → header swaps to brain +
                 "Thought process" but the chip stays expanded; answer
                 text streams below the chip in markdown
              3. SSE finishes → store entry removed, this whole bubble
                 unmounts. The real Zero-replicated message takes over
                 with `metadata.thinking` so the chip persists
          */}
          {!hasThinking && !hasContent ? (
            // Pre-thinking pill — shown for the first ~400ms before any
            // thinking_delta arrives. Brain icon + blue pulsing dot pair
            // replaced with the AnoSpinner so it matches the rest of the
            // app's loading affordances.
            <div className="inline-flex items-center gap-1.5 rounded-full bg-foreground/[0.03] px-2.5 py-1">
              <LoadingIcon size={12} className="text-muted-foreground" />
              <ThinkingLabel className="text-sm leading-relaxed font-normal text-muted-foreground" />
            </div>
          ) : null}
          {hasThinking ? (
            <ThinkingSection
              text={stream.thinking}
              // Header transition: spinner + "Thinking…" while only
              // thinking is streaming, then brain + "Thought process"
              // the moment answer text begins. The chip itself stays
              // expanded across both phases (controlled internally).
              isStreaming={!hasContent && stream.isStreaming}
            />
          ) : null}
          {hasContent
            ? renderMessageContent(
                stream.content,
                workspaceMembers ?? [],
                onUserClick,
                channels,
                onChannelClick,
              )
            : null}
          {stream.error ? (
            <div className="mt-1 text-xs text-destructive">{stream.error}</div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
